import { useTranslations } from "next-intl";
import { useParams } from "react-router-dom";
import { PBanner } from "./PBanner";
import { useExpiringEventPlan } from "@/hooks/useExpiringEventPlan";
import { useEventContext } from "@/providers/EventProvider";
import { useEventCompanyRole } from "@/lib/query/hooks/useEventEntitlements";
import { dismissAtRung, useRungDismissed } from "@/lib/storage/dismissRung";
import {
  expiryRung,
  RUNG_DISMISSIBLE,
  RUNG_ORDINAL,
  RUNG_TONE,
} from "@/lib/ui/expiryTone";

/**
 * "EVENT_PROFESSIONAL" → "Professional". Event plans are always one of the
 * three single-word paid tiers (no `_YEAR` variant — events are one-time
 * purchases), so stripping the `EVENT_` prefix + title-casing is sufficient.
 * Returns "" for null/unknown so the {plan} interpolation degrades gracefully.
 */
function humanPlan(planType: string | null): string {
  if (!planType) return "";
  const bare = planType.replace(/^EVENT_/, "");
  return bare.charAt(0) + bare.slice(1).toLowerCase();
}

/**
 * Event-scoped banner for the 9-month plan lifecycle. Mounted inside the event
 * layout (not the company DashboardShell). Two states:
 * - locked (red): the plan window closed and the event is read-only — renew to edit.
 * - approaching (yellow): expires within 14 days — countdown nudge.
 * Only event OWNER/ADMIN see it. The warning state is dismissible per day; the
 * locked state is not (it reflects a hard read-only condition).
 */
export const EventPlanExpiryBannerHost: React.FC = () => {
  const { show, locked, daysRemaining, plan, eventId: ctxEventId } = useExpiringEventPlan();
  const { eventRole } = useEventContext();
  // The shell's event projection carries no `eventId`, so the renew link used
  // to point at `/user/subscription/event/undefined`. The route has it.
  const { eventId: routeEventId } = useParams<{ eventId: string }>();
  const eventId = routeEventId ?? ctxEventId;
  // Renewing buys a plan for the company that OWNS the event — only its OWNER
  // can; an event OWNER from another company gets the notice without the CTA.
  const companyRole = useEventCompanyRole(eventId ?? null);
  const canRenew = companyRole.isCompanyOwner;
  const t = useTranslations("eventPlanExpiry");

  const role = eventRole?.role ?? "";
  // Same escalating-ordinal persistence as the company strip: one key per
  // event, the day and the silenced rung in the value, so the countdown
  // crossing a threshold re-arms the nudge by itself.
  const dismissKey = `eventPlanExpiryDismiss:${eventId ?? "?"}`;
  const rung = expiryRung(daysRemaining);
  const ordinal = RUNG_ORDINAL[rung];
  const dismissed = useRungDismissed(dismissKey, ordinal);

  if (role !== "OWNER" && role !== "ADMIN") return null;
  if (!locked && (!show || (dismissed && RUNG_DISMISSIBLE[rung]))) return null;

  const planLabel = humanPlan(plan);
  const actionHref = canRenew && eventId ? `/user/subscription/event/${eventId}` : undefined;

  if (locked) {
    return (
      <PBanner
        testId="event-plan-locked-banner"
        tone="danger"
        // A card, not a slab: this sits inside a padded content column and the
        // read-only state is already reinforced by the disabled controls.
        shape="card"
        title={t("lockedTitle")}
        description={t("lockedDescription", { plan: planLabel })}
        actionHref={actionHref}
        actionLabel={canRenew ? t("renewAction") : undefined}
      />
    );
  }

  return (
    <PBanner
      testId="event-plan-expiry-banner"
      rung={rung}
      tone={RUNG_TONE[rung]}
      shape="card"
      title={t("warnTitle", { days: daysRemaining })}
      description={t("warnDescription", { days: daysRemaining, plan: planLabel })}
      onDismiss={
        RUNG_DISMISSIBLE[rung] ? () => dismissAtRung(dismissKey, ordinal) : undefined
      }
      actionHref={actionHref}
      actionLabel={canRenew ? t("renewAction") : undefined}
    />
  );
};
